/* Who is signed in: /auth/me drives the shell, the navigation and every permission check. */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type MeOut } from '../api';
import { ApiError, onSessionEnded } from '../api/client';
import { navigate } from './router';

interface SessionCtx {
  me: MeOut | null;
  loading: boolean;
  refresh: () => Promise<MeOut | null>;
  can: (...perms: string[]) => boolean;
  logout: () => Promise<void>;
  home: () => string;
}

const Ctx = createContext<SessionCtx>({
  me: null, loading: true, refresh: async () => null, can: () => false, logout: async () => undefined, home: () => '/login',
});

export function homeFor(me: MeOut | null): string {
  if (!me) return '/login';
  if (me.user_type === 'student') return '/me';
  if (me.user_type === 'parent') return '/parent';
  if (me.mfa_required && !me.mfa_verified) return '/login/2fa';
  const p = new Set(me.permissions);
  if (p.has('dashboard:management')) return '/dashboard';
  if (p.has('dashboard:warden')) return '/dashboard/warden';
  if (p.has('dashboard:mess')) return '/dashboard/mess';
  if (p.has('leave:gate')) return '/leave/gate';
  if (p.has('support:read')) return '/support';
  return '/account';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeOut | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const m = await api.auth.me();
      setMe(m);
      return m;
    } catch (e) {
      if (!(e instanceof ApiError) || e.status === 401) setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => onSessionEnded(() => {
    setMe(null);
    const portal = window.location.pathname.startsWith('/me') || window.location.pathname.startsWith('/parent');
    navigate(portal ? '/portal/login' : '/login?expired=1', { replace: true });
  }), []);

  const can = useCallback((...perms: string[]) => {
    if (!me) return false;
    if (!perms.length) return true;
    return perms.some((p) => me.permissions.includes(p));
  }, [me]);

  const logout = useCallback(async () => {
    const portal = me?.user_type === 'student' || me?.user_type === 'parent';
    try { await api.auth.logout(); } catch { /* already gone */ }
    setMe(null);
    navigate(portal ? '/portal/login' : '/login', { replace: true });
  }, [me]);

  return <Ctx.Provider value={{ me, loading, refresh, can, logout, home: () => homeFor(me) }}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  return useContext(Ctx);
}

/** Renders children only when the user holds one of the permissions. */
export function Can({ perm, children, fallback = null }: { perm: string | string[]; children: ReactNode; fallback?: ReactNode }) {
  const { can } = useSession();
  return <>{can(...(Array.isArray(perm) ? perm : [perm])) ? children : fallback}</>;
}
